<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { PanelLeft, PanelRight, X } from 'lucide-svelte';
  import TreeRail from './TreeRail.svelte';
  import Canvas from './Canvas.svelte';
  import Inspector from './Inspector.svelte';
  import StatusFooter from './StatusFooter.svelte';
  import AddMenu from './AddMenu.svelte';
  import Modal from '../ui/Modal.svelte';
  import LoadForm from '../forms/LoadForm.svelte';
  import DeviceForm from '../forms/DeviceForm.svelte';
  import { uiState } from '$lib/state/ui.svelte';
  import { router, selectEntity } from '$lib/router';
  import { showSuccess } from '$lib/state/toast.svelte';
  import type { LoadResponse } from '$lib/types/load';
  import type { InputDeviceResponse } from '$lib/types/input_device';

  let showCreateLoadDialog = $state(false);
  let showCreateDeviceDialog = $state(false);

  // Mobile-only drawer state. On >= md both rails are always visible and these
  // are ignored; below md they slide the tree / inspector in over the canvas.
  let mobileRail = $state(false);
  let mobileInspector = $state(false);

  function closeDrawers() {
    mobileRail = false;
    mobileInspector = false;
  }

  // Picking something in the tree should reveal the canvas/inspector on mobile,
  // so collapse the tree drawer whenever the selection changes.
  let selectedEntity = $derived(router.selectedEntity);
  $effect(() => {
    selectedEntity;
    mobileRail = false;
  });

  function handleOpenCreateLoad(e: Event) {
    const detail = (e as CustomEvent).detail as { roomId: number } | undefined;
    uiState.createDialogData = detail?.roomId != null
      ? { load: { room: detail.roomId }, isEditing: false }
      : { load: {}, isEditing: false };
    showCreateLoadDialog = true;
  }

  function handleLoadCreated(load: LoadResponse, count: number) {
    showCreateLoadDialog = false;
    selectEntity('load', load.id);
    showSuccess(count > 1 ? `${count} loads created` : `Load "${load.name}" created`);
  }

  function handleOpenCreateDevice(e: Event) {
    const detail = (e as CustomEvent).detail as { roomId: number } | undefined;
    uiState.createDialogData = detail?.roomId != null
      ? { roomId: detail.roomId }
      : {};
    showCreateDeviceDialog = true;
  }

  function handleDeviceCreated(device: InputDeviceResponse) {
    showCreateDeviceDialog = false;
    selectEntity('keypad', device.id);
    showSuccess(`"${device.name}" created`);
  }

  onMount(() => {
    document.addEventListener('open-create-load', handleOpenCreateLoad);
    document.addEventListener('open-create-device', handleOpenCreateDevice);
  });
  onDestroy(() => {
    document.removeEventListener('open-create-load', handleOpenCreateLoad);
    document.removeEventListener('open-create-device', handleOpenCreateDevice);
  });
</script>

<div class="flex flex-col h-[calc(100vh-40px-24px)] w-full">
  <!-- Mobile-only bar to open the tree / inspector drawers. -->
  <div
    class="md:hidden flex items-center gap-2 px-2 py-1.5 shrink-0 bg-bg-base border-b border-border-default"
  >
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-text-secondary border border-border-default rounded hover:bg-bg-hover transition-colors"
      onclick={() => {
        mobileInspector = false;
        mobileRail = true;
      }}
    >
      <PanelLeft class="w-3.5 h-3.5" />
      Browse
    </button>
    <div class="flex-1"></div>
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-text-secondary border border-border-default rounded hover:bg-bg-hover transition-colors"
      onclick={() => {
        mobileRail = false;
        mobileInspector = true;
      }}
    >
      Details
      <PanelRight class="w-3.5 h-3.5" />
    </button>
  </div>

  <div class="relative flex flex-1 min-h-0 w-full overflow-hidden">
    <aside
      class="absolute inset-y-0 left-0 z-30 w-[280px] max-w-[85vw] bg-bg-rail border-r border-border-default overflow-y-auto transition-transform duration-200 ease-out
      md:static md:z-auto md:w-[260px] md:max-w-none md:translate-x-0 md:shrink-0
      {mobileRail ? 'translate-x-0 shadow-toyama-lg' : '-translate-x-full'}"
    >
      <TreeRail />
    </aside>

    <main class="flex-1 min-w-0 bg-bg-canvas overflow-auto">
      <Canvas />
    </main>

    <aside
      class="absolute inset-y-0 right-0 z-30 w-[320px] max-w-[85vw] bg-bg-rail border-l border-border-default overflow-y-auto transition-transform duration-200 ease-out
      md:static md:z-auto md:max-w-none md:translate-x-0 md:shrink-0
      {mobileInspector ? 'translate-x-0 shadow-toyama-lg' : 'translate-x-full'}"
    >
      <Inspector />
    </aside>

    {#if mobileRail || mobileInspector}
      <button
        type="button"
        class="absolute inset-0 z-20 bg-black/40 md:hidden"
        aria-label="Close panel"
        onclick={closeDrawers}
      ></button>
    {/if}
  </div>
</div>
<StatusFooter />
<AddMenu />

<Modal
  bind:open={showCreateLoadDialog}
  size="lg"
  onclose={() => {
    uiState.createDialogData = null;
  }}
>
  {#snippet header()}
    <div class="flex items-center justify-between w-full">
      <h2 class="text-xl font-semibold text-text-primary">Create Load</h2>
      <button
        type="button"
        class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
        aria-label="Close"
        onclick={() => {
          showCreateLoadDialog = false;
        }}
      >
        <X class="w-5 h-5" />
      </button>
    </div>
  {/snippet}
  {#snippet children()}
    <LoadForm
      onsuccess={handleLoadCreated}
      oncancel={() => {
        showCreateLoadDialog = false;
      }}
    />
  {/snippet}
</Modal>

<Modal
  bind:open={showCreateDeviceDialog}
  size="md"
  onclose={() => {
    uiState.createDialogData = null;
  }}
>
  {#snippet header()}
    <div class="flex items-center justify-between w-full">
      <h2 class="text-xl font-semibold text-text-primary">Create Device</h2>
      <button
        type="button"
        class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
        aria-label="Close"
        onclick={() => {
          showCreateDeviceDialog = false;
        }}
      >
        <X class="w-5 h-5" />
      </button>
    </div>
  {/snippet}
  {#snippet children()}
    <DeviceForm
      onsuccess={handleDeviceCreated}
      oncancel={() => {
        showCreateDeviceDialog = false;
      }}
    />
  {/snippet}
</Modal>
